import type {
  FieldErrors,
  FieldValues,
  UseFormSetFocus,
} from "react-hook-form";

/** `safeParse(undefined)` 가 통과하는 필드는 선택값이다 */
type OptionalityProbe = { safeParse: (value: unknown) => { success: boolean } };

/**
 * zod object 의 shape 에서 필수 필드 이름을 뽑는다.
 *
 * "필수값 누락" 과 "형식 오류" 토스트를 가르는 데 쓴다 — 목록을 손으로 적으면
 * 스키마에서 필드가 사라지거나 선택으로 바뀔 때 조용히 어긋난다.
 */
export function requiredFieldNames(
  shape: Record<string, OptionalityProbe>,
): string[] {
  return Object.entries(shape)
    .filter(([, field]) => !field.safeParse(undefined).success)
    .map(([name]) => name);
}

/** 포커스가 옮겨간 뒤 스크롤을 시작할 때까지의 여유. 필드가 접힌 섹션 안이면 펼쳐질 시간이 필요하다 */
const FOCUS_SCROLL_DELAY_MS = 100;

/**
 * 폼 검증 실패 시 첫 번째 에러 필드로 포커스를 옮기고 화면 가운데로 스크롤한다.
 *
 * `setFocus` 가 필수인 이유: RHF 가 등록한 `field.ref` 를 거치지 않으면 어느 DOM 요소를
 * 포커스해야 하는지 알 수 없어 아무 일도 일어나지 않는다.
 *
 * @param scrollContainer 지정하면 그 컨테이너 안에서 스크롤한다 (드로어·모달 본문)
 */
export function focusFirstErrorField<T extends FieldValues>(
  errors: FieldErrors<T>,
  setFocus: UseFormSetFocus<T>,
  { scrollContainer }: { scrollContainer?: HTMLElement | null } = {},
): void {
  const firstErrorKey = Object.keys(errors)[0];
  if (!firstErrorKey) return;

  // 레이아웃이 확정된 다음 프레임에 포커스해야 field.ref 가 실제 요소를 가리킨다
  requestAnimationFrame(() => {
    setFocus(firstErrorKey as never);

    setTimeout(() => {
      const focused = document.activeElement as HTMLElement | null;
      if (!focused) return;

      if (!scrollContainer) {
        focused.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = focused.getBoundingClientRect();
      scrollContainer.scrollTo({
        top:
          scrollContainer.scrollTop +
          (elementRect.top - containerRect.top) -
          containerRect.height / 2,
        behavior: "smooth",
      });
    }, FOCUS_SCROLL_DELAY_MS);
  });
}

/**
 * 폼 필드 값의 동등 비교. undefined 와 null 은 둘 다 "값 없음" 으로 같게 본다.
 *
 * 배열·객체를 값으로 비교하는 것이 요점이다 — 원본은 `getDefaultValues()` 가 호출마다
 * 새로 만들어 주므로 참조 비교로는 내용이 같아도 항상 "변경됨" 이 된다
 * (충전기의 `connectors` 가 그래서 매번 변경으로 잡혔다).
 */
function isSameFieldValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;

  const left = a ?? null;
  const right = b ?? null;
  if (left === null || right === null) return left === right;

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    return (
      left.length === right.length &&
      left.every((item, index) => isSameFieldValue(item, right[index]))
    );
  }

  if (left instanceof Date || right instanceof Date) {
    return (
      left instanceof Date &&
      right instanceof Date &&
      left.getTime() === right.getTime()
    );
  }

  if (typeof left === "object" && typeof right === "object") {
    // 키 합집합으로 돌려 "없는 키" 와 "undefined 인 키" 를 같게 본다
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    for (const key of keys) {
      if (
        !isSameFieldValue(
          (left as Record<string, unknown>)[key],
          (right as Record<string, unknown>)[key],
        )
      ) {
        return false;
      }
    }
    return true;
  }

  return false;
}

/**
 * 원본 객체와 현재 객체를 비교하여 변경된 필드만 반환
 * undefined와 null은 동일하게 취급 (둘 다 "값 없음"으로 간주)
 * 배열·객체 필드는 참조가 아니라 값으로 비교한다
 * @param original - 원본 객체
 * @param current - 현재 객체
 * @returns 변경된 필드만 포함하는 Partial 객체
 */
export function getChangedFields<T extends Record<string, unknown>>(
  original: T,
  current: T,
): Partial<T> {
  const changes: Partial<T> = {};

  for (const key of Object.keys(current) as (keyof T)[]) {
    if (!isSameFieldValue(original[key], current[key])) {
      changes[key] = current[key];
    }
  }

  return changes;
}
